import {
  Button,
  DateRangePicker,
  Icon,
  IconSizeClasses,
  VirtualizedTable,
} from '@eppendorf/vnls-react-components';
import { StabilityReportOutput } from '@eppendorf/vnls-reporting-service-utils';
import { createColumnHelper } from '@tanstack/react-table';
import { type ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError, useSearchParams } from 'react-router-dom';

import { trackPendoEvent } from '$shared/tracking/trackPendoEvent';
import { formatDateRange } from '$shared/utils/date.utils';

import { CellTimestamp } from '$components/cell-timestamp/cell-timestamp';
import { DeviceNameCell } from '$components/device/name-cell';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { SearchInput } from '$components/search-input/search-input';
import { CopyHashCell } from '$features/report-hub/components/copy-hash-cell';
import { DeleteReportConfirmWrapper } from '$features/report-hub/components/delete-report-confirm-wrapper';
import { ReportListEmptyState } from '$features/report-hub/components/report-list-empty-state';
import { ReportStatusBadge } from '$features/report-hub/components/report-status-badge';
import {
  ReportStatusFilter,
  ReportStatusFilterComponent,
} from '$features/report-hub/components/status-filter';
import {
  useDownloadReport,
  useInfiniteStabilityReports,
} from '$features/report-hub/report-hub.api';
import { formatFileSize } from '$features/report-hub/report-hub.utils';
import { useReportListFilters } from '$features/report-hub/use-report-list-filters';

import './stability-report-list.scss';

export function StabilityReportList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleRowClick = useCallback(
    (report: StabilityReportOutput) => {
      navigate({ search: `?sidecarRoute=stability-report/detail&id=${report.id}` });
    },
    [navigate],
  );

  const {
    apiStatus,
    apiSearchQuery,
    from,
    to,
    statusFilter,
    searchQuery,
    dateRange,
    selectedQuickOption,
    isDeleteReportDialogOpen,
    selectedReport,
    setIsDeleteReportDialogOpen,
    copiedHashReportId,
    handleSearchQueryChange,
    handleDateRangeChange,
    handleQuickSelectOption,
    handleDeleteReport,
    handleCopyHash,
    handleStatusChange,
    resetSearch,
  } = useReportListFilters();

  const { data, isError, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useInfiniteStabilityReports(apiSearchQuery, apiStatus, from, to);

  const downloadReportMutation = useDownloadReport();

  const handleDownloadReport = useCallback(
    async (reportId: string) => {
      try {
        const downloadUrl = await downloadReportMutation.mutateAsync(reportId);
        window.open(downloadUrl, '_blank');
      } catch (error) {
        // Error handling can be added here if needed
        // eslint-disable-next-line no-console -- intentional error logging
        console.error('Failed to download report:', error);
      }
    },
    [downloadReportMutation],
  );

  // Flatten the pages - API returns arrays directly
  const reports = data?.pages?.flatMap((page) => page || []) ?? [];
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    statusFilter !== ReportStatusFilter.All ||
    Boolean(dateRange.from) ||
    Boolean(dateRange.to);
  const shouldShowNoDataEmptyState = !hasActiveFilters && reports.length === 0;

  const routeError = useRouteError();

  const columnHelper = useMemo(() => createColumnHelper<StabilityReportOutput>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('status', {
        header: () => t('shared.status'),
        meta: {
          dataLabel: t('shared.status'),
        },
        id: 'status',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => <ReportStatusBadge status={info.getValue()} />,
      }),
      columnHelper.display({
        header: () => t('reportHub.table.device'),
        meta: {
          dataLabel: t('reportHub.table.device'),
        },
        id: 'device',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => (
          <DeviceNameCell
            device={{
              name: info.row.original.deviceName ?? undefined,
              model: info.row.original.deviceModel ?? undefined,
              serialNumber: info.row.original.serialNumber ?? '',
              manufacturer: 'Eppendorf',
            }}
          />
        ),
      }),
      columnHelper.display({
        header: () => t('reportHub.table.reportPeriod'),
        meta: {
          dataLabel: t('reportHub.table.reportPeriod'),
        },
        id: 'reportPeriod',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          return formatDateRange(report.startDate, report.endDate);
        },
      }),
      columnHelper.accessor('requestedBy', {
        header: () => t('reportHub.table.requestedBy'),
        meta: {
          dataLabel: t('reportHub.table.requestedBy'),
        },
        id: 'requestedBy',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => (
          <span
            className="ellipsize ellipsize--wrap-on-mobile"
            title={info.getValue() ?? ''}
          >
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: () => t('reportHub.table.generated'),
        meta: {
          dataLabel: t('reportHub.table.generated'),
        },
        id: 'generated',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const timestamp = info.getValue();
          return timestamp ? <CellTimestamp timestamp={timestamp} /> : '-';
        },
      }),
      columnHelper.accessor('hash', {
        header: () => t('reportHub.table.sha256Hash'),
        meta: {
          dataLabel: t('reportHub.table.sha256Hash'),
        },
        id: 'hash',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const hash = info.getValue();
          const report = info.row.original;
          if (!hash) return '-';

          return (
            <CopyHashCell
              hash={hash}
              reportId={report.id}
              copiedHashReportId={copiedHashReportId}
              onCopy={handleCopyHash}
            />
          );
        },
      }),
      columnHelper.accessor('fileSize', {
        header: () => t('reportHub.table.fileSize'),
        meta: {
          dataLabel: t('reportHub.table.fileSize'),
        },
        id: 'fileSize',
        cell: (info) => formatFileSize(info.getValue()),
      }),
      columnHelper.display({
        header: () => t('reportHub.table.download'),
        meta: {
          dataLabel: t('reportHub.table.download'),
        },
        id: 'download',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          if (report.status === 'Ready') {
            return (
              <Button
                size="small"
                variant="icon"
                onClick={(e) => {
                  // Manual Pendo tracking before stopping propagation
                  trackPendoEvent('download', { reportId: report.id });
                  e.stopPropagation();
                  handleDownloadReport(report.id);
                }}
                aria-label={t('stabilityDetail.actions.downloadReportAriaLabel')}
                disabled={downloadReportMutation.isPending}
              >
                <Icon name="save" size={IconSizeClasses.Small} />
              </Button>
            );
          }
          return <span>-</span>;
        },
      }),
      columnHelper.display({
        header: () => t('shared.actions'),
        meta: {
          dataLabel: t('shared.actions'),
        },
        id: 'actions',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: ({ row }) => (
          <Button
            onClick={(e) => {
              handleDeleteReport(e, row.original);
            }}
            variant="icon"
          >
            <Icon name="delete" size={IconSizeClasses.XSmall} />
          </Button>
        ),
      }),
    ],
    [
      copiedHashReportId,
      downloadReportMutation.isPending,
      handleCopyHash,
      handleDownloadReport,
      t,
    ],
  );

  return (
    <>
      <div className="stability-report-list__header">
        <ReportStatusFilterComponent
          selectedStatus={statusFilter}
          onStatusChange={handleStatusChange}
          className="report-status-filter-component"
        />
        <DateRangePicker
          dateRange={{
            from: dateRange.from,
            to: dateRange.to,
          }}
          onDateRangeChange={handleDateRangeChange}
          autoFocus
          disabled={{
            after: new Date(),
          }}
          quickSelect={{
            enabled: true,
            onOptionSelect: handleQuickSelectOption,
            selectedOption: selectedQuickOption,
          }}
        />
        <SearchInput
          onSearchQueryChange={handleSearchQueryChange}
          placeholder={t('reportHub.search.placeholder')}
          id="stability-report-search"
          value={searchQuery}
          className="stability-report-search bg-white"
        />
      </div>

      {(routeError || isError) && <LoadingErrorHint />}

      {!isLoading && !isError && !shouldShowNoDataEmptyState && (
        <div
          className="flex flex__dir--column"
          style={{
            height: reports && reports.length > 0 ? '100vh' : '50vh',
          }}
        >
          <VirtualizedTable
            className="stability-report-table"
            data={reports}
            columns={columns}
            onRowClick={handleRowClick}
            isSelectedRow={(report) => searchParams.get('id') === report.id}
            loadMore={() => {
              if (hasNextPage && !isFetching) {
                fetchNextPage();
              }
            }}
            isLoading={isFetching}
            onResetSearch={resetSearch}
            isTableToCardDesign
          />
        </div>
      )}

      {!isLoading && !isError && shouldShowNoDataEmptyState && (
        <ReportListEmptyState
          title={t('reportHub.emptyState.stability.title')}
          description={t('reportHub.emptyState.stability.description')}
          actionLabel={t('reportHub.emptyState.stability.action')}
          onActionClick={() => navigate('/monitoring')}
        />
      )}

      <DeleteReportConfirmWrapper
        isDeleteConfirmOpen={isDeleteReportDialogOpen}
        onOpenChange={setIsDeleteReportDialogOpen}
        selectedReport={selectedReport}
      />
    </>
  );
}
